import { Fragment, useState } from "react"
import { assocPath } from "ramda"
import { useOutletContext } from "react-router-dom"
import { styled } from "@mui/material/styles"
import {
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  Box,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
} from "@mui/material"

import { CloseOutlined } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import CartNotification from "@features/cart/components/cart-notification"
import { openWhatsAppChat } from "./utils"

import { useDispatch } from "react-redux"
import { TABBAR_HEIGHT, handleNavigation } from "./utils"
import { useGetChat, useAppState } from "@hooks/index"
import useGetMenuList from "./use-get-menu-list"
import { GlobalProps } from "@layout/index"

interface SubmenuProps {
  id: number
  title: string
  route: string
}

interface MenuProps {
  show?: (page: string) => void
  jumpTo?: (page: string) => void
}

const CustomBottomNavigation = styled(BottomNavigation)(
  ({ theme }) => `
    position: fixed ;
    width: 100%;
    height:  ${TABBAR_HEIGHT}px; 
    bottom: 0;
    left: 0 ;
    right: 0;
    padding-bottom: 16px;
    padding-top: 24px;
    border-top: 1px solid ${theme.palette.divider};

    ${theme.breakpoints.up("md")}{ 
        display: none ;
      }
`
)

const MenuList = ({ menu, value, toggleDrawer, fn, ...props }: any) => {
  const { setAuth } = useOutletContext<GlobalProps>()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const itsChatMenu = (menu: any, submenu: any) =>
    menu.menu === "0" && submenu.id === 3

  const handleChat = () => {
    fn.isChatMaximized() ? fn.minimize() : fn.maximize()
  }

  return (
    <Box
      role="presentation"
      onClick={toggleDrawer(value, true)}
      onKeyDown={toggleDrawer(value, true)}
      sx={(theme) => ({ zIndex: theme.zIndex.appBar })}
    >
      <List>
        <ListItem
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            color: theme.palette.text.disabled,
          })}
        >
          <Typography
            variant="h6"
            sx={(theme) => ({
              padding: "0px 16px",
            })}
          >
            {menu.label}
          </Typography>
          <CloseOutlined />
        </ListItem>
        {menu.submenus.map((value: SubmenuProps, index: number) => (
          <Fragment key={index}>
            <ListItem>
              <ListItemButton
                sx={{ padding: "0px 16px" }}
                onClick={() =>
                  itsChatMenu(menu.menu, menu.submenus)
                    ? handleChat()
                    : handleNavigation(
                        dispatch,
                        value.route,
                        navigate,
                        props.jumpTo,
                        setAuth
                      )
                }
              >
                <Stack gap="32px" direction="row" alignItems="center">
                  <ListItemText primary={value.title} />

                  {menu.menu === "3" &&
                    value.id === 0 && ( // if it's cart, add the badge
                      <CartNotification />
                    )}
                </Stack>
              </ListItemButton>
            </ListItem>
          </Fragment>
        ))}
      </List>
    </Box>
  )
}

export const Tabbar = (props: MenuProps) => {
  const { menuList, adminMenuList } = useGetMenuList()
  const userMenuList = adminMenuList ? [...menuList, adminMenuList] : menuList
  const { user } = useAppState()

  const [value, setValue] = useState(0)
  const [state, setState] = useState<any>(userMenuList)
  const [menu, setMenu] = useState<any>()

  const { ChatComponent, fn } = useGetChat()

  const handleChat = (name: string, email: string) => {
    openWhatsAppChat({ name, email })
    // fn.isChatMaximized() ? fn.minimize() : fn.maximize()
  }

  const toggleDrawer =
    (item: number, status: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return
      }

      const menuStatus = state[item].open === true ? !status : status
      setState(assocPath([item, "open"], menuStatus, userMenuList))
      setMenu(userMenuList[item])
    }

  return (
    <>
      <Drawer
        anchor={"bottom"}
        open={state[value].open}
        onClose={toggleDrawer(value, false)}
        sx={(theme) => ({
          zIndex: theme.zIndex.fab,
        })}
        ModalProps={{
          disablePortal: true,
          BackdropProps: {
            onClick: () => {
              const closed = state
              closed[value].open = false
              setState(closed)
              setMenu({ ...menu, open: false })
            }, // Add this to handle backdrop clicks
          },
        }}
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <Box sx={{ mb: 11 }}>
          <MenuList
            menu={menu}
            value={value}
            toggleDrawer={toggleDrawer}
            fn={fn}
            {...props}
          />
        </Box>
      </Drawer>

      <CustomBottomNavigation
        showLabels
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue)
        }}
        sx={(theme) => ({ zIndex: theme.zIndex.drawer, width: "100%" })}
      >
        {Object.values(userMenuList).map((menu, i) => (
          <BottomNavigationAction
            key={menu.menu}
            label={menu.label}
            icon={
              menu.menu === "3" ? (
                <CartNotification variant="dot">{menu.icon}</CartNotification>
              ) : (
                <> {menu.icon}</>
              )
            }
            onClick={
              menu.menu === "2"
                ? () => handleChat(user?.name, user?.email)
                : toggleDrawer(i, true)
            }
            disableRipple
            sx={{ fontWeight: 700 }}
          />
        ))}
      </CustomBottomNavigation>
      <ChatComponent />
    </>
  )
}

export default Tabbar
